import {
    ChangeEvent,
    ComponentType,
    HTMLAttributes,
    useMemo,
    useState
} from "react"
import { Autocomplete, SingleLineTextInput } from "dash-styled"
import { JSONSchema7, JSONSchema7Array } from "json-schema"

import {
    DataFieldType,
    DocumentType
} from "../../../../../../Models/DataProgram/DocumentExtraction"
import { SchemaFormModal } from "../../../Modals"

type DocumentModalProps = {
    onClose: () => void
    onSubmit: (value: DocumentType) => void
    schema?: JSONSchema7
    value?: DocumentType
    mode?: "modal" | "paper"
}

type ExtendedJSONSchema = JSONSchema7 & {
    discriminator: any
}

const Paper: ComponentType<HTMLAttributes<HTMLElement>> = ({ children }) => (
    <div className="mt-1 pt-1 pl-1 bg-white border border-rhino-300 rounded-lg">
        {children}
    </div>
)

export const DocumentModal = ({
    onClose,
    onSubmit,
    schema,
    value,
    mode = "modal"
}: DocumentModalProps) => {
    const [documentType, setDocumentType] = useState<
        [string, string] | undefined
    >(
        value?.documentType
            ? [
                  value.documentType,
                  (
                      schema?.definitions
                          ?.DocumentParameterModel as ExtendedJSONSchema
                  )?.discriminator?.mapping?.[value.documentType]
              ]
            : undefined
    )

    const [id, setId] = useState<string | undefined>(value?.documentId)

    const handleChange = (e: any, value: [string, unknown] | null) => {
        const type = value as [string, string]
        if (type) {
            setDocumentType(type)
            const documentModelName = type[1].split("/").pop() || ""
            const documentProperties = (
                schema?.definitions?.[documentModelName] as JSONSchema7
            )?.properties
            setId(
                (documentProperties?.documentId as JSONSchema7)
                    .default as string
            )
        }
    }

    const handleIdChange = (e: ChangeEvent<HTMLInputElement>) => {
        setId(e.target.value)
    }

    const handleSubmit = () => {
        if (documentType && id) {
            const documentModelName = documentType[1].split("/").pop() || ""
            const documentProperties = (
                schema?.definitions?.[documentModelName] as JSONSchema7
            )?.properties
            const defaultAnnotationFields = [
                ...(((documentProperties?.annotationFields as JSONSchema7)
                    .default as JSONSchema7Array) || [])
            ]
            const defaultPresetField = [
                ...(((documentProperties?.presetFields as JSONSchema7)
                    .default as JSONSchema7Array) || [])
            ]
            onSubmit({
                ...value,
                documentType: documentType[0],
                documentId: id,
                annotationFields: defaultAnnotationFields as DataFieldType[],
                presetFields: defaultPresetField as DataFieldType[]
            })
        }
    }

    const options = useMemo(() => {
        return Object.entries(
            (schema?.definitions?.DocumentParameterModel as ExtendedJSONSchema)
                ?.discriminator?.mapping
        )
    }, [schema?.definitions?.DocumentParameterModel])

    return (
        <SchemaFormModal
            testId="document-modal"
            mode={mode}
            title={value ? "Edit document" : "Add new document"}
            onClose={onClose}
            onSubmit={handleSubmit}
            submit={{
                label: value ? "Edit document" : "Add document",
                disabled: !documentType || !id
            }}
        >
            <div className="flex justify-between items-center">
                <span>Document Name*</span>
                <Autocomplete
                    className="w-2/3"
                    options={options}
                    value={documentType}
                    getOptionLabel={([label]) => label}
                    onChange={handleChange}
                    PaperComponent={Paper}
                    ListboxProps={{
                        "data-testid": "documents-name-select-list"
                    }}
                    classes={{
                        option: "hover:bg-grape-30 p-0 bg-transparent"
                    }}
                    renderOption={([label], { selected }) => (
                        <div
                            className={`w-full px-4 py-1.5 text-sm leading-5 ${
                                selected
                                    ? "bg-grape-40 text-black"
                                    : "text-gray-700"
                            }`}
                        >
                            {label}
                        </div>
                    )}
                    renderInput={({ inputProps, InputProps }) => (
                        <div ref={InputProps.ref}>
                            <SingleLineTextInput
                                {...inputProps}
                                testId="document-name-select"
                                inputClassName="rounded-lg border-rhino-300 bg-transparent py-2 px-3 text-sm leading-5 outline-none"
                            />
                        </div>
                    )}
                />
            </div>
            <div className="flex justify-between items-center mt-2">
                <span>ID</span>
                <SingleLineTextInput
                    disabled
                    required
                    testId="document-id-input"
                    value={id || ""}
                    onChange={handleIdChange}
                    className="rounded-lg border-rhino-300 w-2/3"
                    inputClassName="bg-transparent py-2 px-3 text-sm leading-5"
                />
            </div>
        </SchemaFormModal>
    )
}
